import { getStationByPostalCode, calculateDistance } from './postalCode.js';
import { majorStations, minorStations } from "../data/stations.js";

// --- Helpers ---
const isNear = (station) => station.type && station.type.startsWith("near");
const isUnknown = (station) => station.type && station.type === "unknown";
const isMajor = (station) => station.type === "major";
const isMinor = (station) => station.type === "minor";

/**
 * Heuristic function for A* (Haversine distance between current and goal)
 */
function heuristic(station, goalStation) {
    if (!station.centerLat || !station.centerLng || !goalStation.centerLat || !goalStation.centerLng) {
        return 0; // fallback if coordinates missing
    }
    return calculateDistance(station.centerLat, station.centerLng, goalStation.centerLat, goalStation.centerLng);
}

/** 
 * Get all connections for a station, including reverse connections.
 * This helps in finding neighbors in the A* algorithm.
 * @param {Object} current - Current station object 
 * @param {Array} allStations - List of all stations to check for reverse connections
 * @returns {Array} List of postal codes that are directly connected to the current station
 */
function getConnectionsWithReverse(current, allStations) {
    // Direct connections from the current station
    const direct = Array.isArray(current.connections) && current.connections.length > 0
        ? current.connections
        : (Array.isArray(current.connectedToMajorStops) && current.connectedToMajorStops.length > 0
            ? current.connectedToMajorStops
            : []);

    // All the stations that connect to the current station
    const reverse = allStations
        .filter(station => {
            const conns = Array.isArray(station.connections) && station.connections.length > 0
                ? station.connections
                : (Array.isArray(station.connectedToMajorStops) && station.connectedToMajorStops.length > 0
                    ? station.connectedToMajorStops
                    : []);
            return conns.includes(current.postalCode);
        })
        .map(station => station.postalCode);

    return Array.from(new Set([...direct, ...reverse]));
}

/**
 * Build the optimal route using A* from source to destination postal code.
 * @param {string} postalCodeFrom
 * @param {string} postalCodeTo
 * @returns {Array|null} Ordered list of stations or null if no route found
 */
export function buildRouteAStar(postalCodeFrom, postalCodeTo) {
    const start = getStationByPostalCode(postalCodeFrom);
    const finalGoal = getStationByPostalCode(postalCodeTo);

    if (!start || !finalGoal) return null;

    let goal = finalGoal;
    let appendLastMile = false;

    if ((isNear(finalGoal) || isUnknown(finalGoal)) && start.postalCode !== finalGoal.postalCode && !getConnectionsWithReverse(start, minorStations.concat(majorStations)).includes(finalGoal.postalCode)) {
        const closestOrNearestStation = getStationByPostalCode(finalGoal.connections?.[0]);
        if (!closestOrNearestStation) return null;
        goal = closestOrNearestStation;
        appendLastMile = true;
    }

    const allStations = minorStations.concat(majorStations);
    const openSet = new Set([start.postalCode]);
    const cameFrom = {};
    const gScore = { [start.postalCode]: 0 };
    const fScore = { [start.postalCode]: heuristic(start, goal) };
    let queue = [{ station: start, f: fScore[start.postalCode] }];

    while (queue.length > 0) {
        queue.sort((a, b) => a.f - b.f);
        const current = queue.shift().station;

        if (current.postalCode === goal.postalCode) {
            const path = [current];
            let prev = cameFrom[current.postalCode];
            while (prev) {
                path.unshift(prev);
                prev = cameFrom[prev.postalCode];
            }
            if (appendLastMile) {
                //path.push(finalGoal);
                path[path.length - 1] = finalGoal;   
            }
            return path;
        }

        openSet.delete(current.postalCode);

        const neighborCodes = getConnectionsWithReverse(current, allStations);
        if (!neighborCodes.length) continue;

        for (const neighborCode of neighborCodes) {
            const neighbor = getStationByPostalCode(neighborCode);
            if (!neighbor) continue;

            // Helpers for readability
            const isFromMajor = isMajor(current);
            const isToMajor = isMajor(neighbor);
            const isFromMinor = isMinor(current);
            const isToMinor = isMinor(neighbor);
            const isFromNear = isNear(current);
            const isToNear = isNear(neighbor);
            const isFromUnknown = isUnknown(current);
            const isToUnknown = isUnknown(neighbor);

            // Rule 1 : unknown and near station only at start or end
            if (isToNear || isToUnknown) {
                if (!isNear(goal) && !isUnknown(goal)) continue;
                if (
                    current.postalCode !== start.postalCode &&
                    neighbor.postalCode !== goal.postalCode
                ) continue;
            } else if (isFromNear || isFromUnknown) {
                if (current.postalCode !== start.postalCode) continue;
            }

            // Rule 2: Classic transitions and weighting
            let penalty = 0;
            if (isFromMajor && isToMajor) {
                penalty = 0;
            } else if (
                (isFromMajor && isToMinor) ||
                (isFromMinor && isToMajor)
            ) {
                penalty = 1;
            } else if (isFromMinor && isToMinor) {
                penalty = 2;
            } else if (isFromNear || isToNear || isFromUnknown || isToUnknown) {
                // already handled above
            } else {
                continue;
            }

            let helsinkiBonus = 0;
            if (neighbor.postalCode === "00100" && neighbor.postalCode !== goal.postalCode) {
                helsinkiBonus = -90; // bonus to favor Helsinki
            }
            const tentativeG = gScore[current.postalCode] +
                calculateDistance(
                    current.centerLat, current.centerLng,
                    neighbor.centerLat, neighbor.centerLng
                ) + penalty + helsinkiBonus;

            if (tentativeG < (gScore[neighbor.postalCode] ?? Infinity)) {
                cameFrom[neighbor.postalCode] = current;
                gScore[neighbor.postalCode] = tentativeG;
                fScore[neighbor.postalCode] = tentativeG + heuristic(neighbor, goal);

                if (!openSet.has(neighbor.postalCode)) {
                    openSet.add(neighbor.postalCode);
                    queue.push({ station: neighbor, f: fScore[neighbor.postalCode] });
                }
            }
        }
    }

    return null;
}

/**
 * Given a route (array of stations), return a detailed step-by-step breakdown.
 * @param {Array} route - Ordered list of stations
 * @returns {Array} Array of steps with action and station info
 */
export function detailRouteSteps(route) {
    if (!route || route.length < 2) return [];

    const steps = [];

    for (let i = 0; i < route.length - 1; i++) {
        const from = route[i];
        const to = route[i + 1];

        const isFromMajor = isMajor(from);
        const isToMajor = isMajor(to);
        const isFromMinor = isMinor(from);
        const isToMinor = isMinor(to);
        const isFromNear = isNear(from);
        const isToNear = isNear(to);
        const isFromUnknown = isUnknown(from);
        const isToUnknown = isUnknown(to);

        let action = "bus";
        if (isFromMajor && isToMajor) {
            action = "bus";
        } else if (
            (isFromMajor && isToMinor) ||
            (isFromMinor && isToMajor)
        ) {
            action = "buschange";
        } else if (
            (isFromMajor && (isToNear || isToUnknown)) ||
            (isFromMinor && (isToNear || isToUnknown))
        ) {
            action = "lastmile";
        } else if (
            ((isFromUnknown || isFromNear) && isToMinor) ||
            ((isFromUnknown || isFromNear) && isToMajor)
        ) {
            action = "firstmile";
        }

        steps.push({
            from: { postalCode: from.postalCode, type: from.type, name: from.name, centerLat: from.centerLat, centerLng: from.centerLng },
            to: { postalCode: to.postalCode, type: to.type, name: to.name, centerLat: to.centerLat, centerLng: to.centerLng },
            action,
        });
    }
    return steps;
}